<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../../models/column.types'
import type { ReportRow } from '../../../models/report.types'
import { readWindowCells } from '../../../service/structuredCells'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui/icon'

const { t } = useI18n()

const props = defineProps<{
  row: ReportRow
  column: ReportColumn
}>()

const format = useFormat()
const cells = computed(() => readWindowCells(props.row[props.column.field]))

function volume(value: number | string): string {
  return typeof value === 'number' ? format.formatCompact(value) : value
}
</script>

<template>
  <div class="report-window-cells flex min-w-max items-center gap-3xs">
    <template v-if="cells.length">
      <span
        v-for="(cell, index) in cells"
        :key="`${cell.label}-${index}`"
        class="flex items-end gap-3xs"
      >
        <span class="flex flex-col items-center">
          <span class="report-window-cells__label" :data-peak="cell.isPeak ? '' : undefined">
            {{ cell.label }}
          </span>
          <span
            class="report-window-cells__pill flex min-w-[3.25rem] flex-col items-center rounded-sm px-2xs py-3xs text-center font-semibold"
            :data-peak="cell.isPeak ? '' : undefined"
          >
            {{ volume(cell.volume) }}
          </span>
        </span>
        <Icon
          v-if="index < cells.length - 1"
          class="report-window-cells__arrow mb-3xs shrink-0"
          name="arrow-right"
          :size="10"
        />
      </span>
    </template>
    <span v-else class="report-window-cells__empty text-xs">{{
      t('reports.table.cellUnavailable')
    }}</span>
  </div>
</template>

<style scoped>
.report-window-cells__label {
  color: var(--ui-color-muted);
  font-size: 0.5625rem;
  font-weight: 600;
}
.report-window-cells__label[data-peak] {
  color: var(--ui-color-chip-orange);
}
.report-window-cells__pill {
  background: var(--ui-color-chip-indigo-soft);
  color: var(--ui-color-chip-indigo);
  font-size: 0.625rem;
  line-height: 1.25;
}
.report-window-cells__pill[data-peak] {
  border: 2px solid var(--ui-color-chip-orange);
  background: var(--ui-color-chip-indigo);
  color: var(--ui-color-surface);
}
.report-window-cells__arrow {
  color: var(--ui-color-muted);
}
.report-window-cells__empty {
  color: var(--ui-color-muted);
}
</style>
